import { createLazyOrchestrator } from 'owa-bundling';
import { lazyInvokeSuggestions } from 'owa-calendar-search-orchestration';
import getSuggestionQueryString from 'owa-calendar-search-orchestration/lib/selectors/getSuggestionQueryString';
import { getGuid } from 'owa-guid';
import { SuggestionKind } from 'owa-search-service/lib/data/schema/SuggestionSet';
import {
    lazyIsQFRequestIdEqualToLatest,
    lazySetLatestQFRequestId,
    is3SServiceAvailable,
} from 'owa-search';
import { getSuggestions } from 'owa-search-actions';
import { SearchScenarioId, getScenarioStore } from 'owa-search-store';

const DEBOUNCE_DELAY_MS = 150;

export const getSuggestionsOrchestrator = createLazyOrchestrator(
    getSuggestions,
    'CLONE_GET_CALENDAR_SUGGESTIONS',
    async actionMessage => {
        const { scenarioId } = actionMessage;

        if (scenarioId !== SearchScenarioId.Calendar) {
            return;
        }

        if (!is3SServiceAvailable()) {
            return;
        }

        // If no QF suggestions are enabled, return.
        const supportedSuggestionKinds: SuggestionKind[] = [
            SuggestionKind.Keywords,
            SuggestionKind.People,
        ];

        const searchStore = getScenarioStore(scenarioId);

        // Gets query text from the store (user inputted text).
        const searchTextForSuggestion = searchStore.searchTextForSuggestion;

        // Gets query string from the store (user inputted text + pills).
        const queryStringForSuggestions = getSuggestionQueryString(scenarioId);

        /**
         * If we have a query string (so we're not in zero query scenario) and
         * QF is enabled, generate a GUID to serve as the request ID and set it in
         * the store.
         */
        let currentQfRequestId = getGuid();
        const setLatestQfRequestId = await lazySetLatestQFRequestId.import();
        setLatestQfRequestId(currentQfRequestId, scenarioId);

        const isQFRequestIdEqualToLatest = await lazyIsQFRequestIdEqualToLatest.import();

        setTimeout(async () => {
            /**
             * Do not issue QF request if:
             * - Query text doesn't match the query text in the store
             * - Too many requests have been issued in succession (i.e. user typing away)
             *   - The exception here is zero query. Because we clear the currentQfRequestId
             *     (because zero query isn't served by QF), it won't be equal to
             *     the latest QF request ID in the store.
             */
            if (
                searchTextForSuggestion === searchStore.searchTextForSuggestion &&
                (isQFRequestIdEqualToLatest(currentQfRequestId, scenarioId) ||
                    currentQfRequestId === null)
            ) {
                let invokeSuggestions = await lazyInvokeSuggestions.import();
                invokeSuggestions(queryStringForSuggestions, supportedSuggestionKinds);
            }
        }, DEBOUNCE_DELAY_MS);
    }
);
